/**
 * Shared credential mode picker + per-mode field set used by both
 * `/build/wrappers/<slug>/credentials` (manage existing) and the inline
 * drawer in WrapperEditor (set-during-create flow).
 *
 * The component is dumb: it reads `mode` and `fields` props, fires
 * `onChange` callbacks. Submission, encryption, and the network round-
 * trip are the caller's responsibility.
 */
import { type JSX } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import type { WrapperCredentialMode, WrapperCredentialView } from '@/api/index.js';
import { Required } from '@/components/console/Required.js';

export const CRED_MODE_LABELS: Record<WrapperCredentialMode, string> = {
  none: 'None (public API)',
  bearer: 'Static bearer token',
  api_key_header: 'API key in header',
  api_key_query: 'API key in query string',
  basic: 'HTTP basic auth',
  oauth_client_credentials: 'OAuth 2.0 (client credentials)',
  custom_headers: 'Custom headers',
};
export const CRED_MODES = Object.keys(CRED_MODE_LABELS) as WrapperCredentialMode[];

export type CredFields = Record<string, string | Record<string, string>>;

export function ModePicker({
  mode,
  setMode,
}: {
  mode: WrapperCredentialMode;
  setMode: (m: WrapperCredentialMode) => void;
}): JSX.Element {
  return (
    <label style={{ display: 'block', marginBottom: 8 }}>
      <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
        Auth mode
        <Required />
      </span>
      <select
        value={mode}
        onChange={(e) => setMode(e.target.value as WrapperCredentialMode)}
        style={inputStyle}
      >
        {CRED_MODES.map((m) => (
          <option key={m} value={m}>
            {CRED_MODE_LABELS[m]}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ModeFields({
  mode,
  fields,
  setFields,
  saved,
}: {
  mode: WrapperCredentialMode;
  fields: CredFields;
  setFields: (next: CredFields) => void;
  saved?: WrapperCredentialView | null;
}): JSX.Element | null {
  const set = (k: string, v: string | Record<string, string>): void =>
    setFields({ ...fields, [k]: v });

  switch (mode) {
    case 'none':
      return (
        <p className="subtle" style={{ fontSize: 13 }}>
          No fields. The wrapper will hit the upstream without any auth.
        </p>
      );
    case 'bearer':
      return (
        <FormField label="Bearer token" required>
          <input
            type="password"
            value={String(fields.token ?? '')}
            onChange={(e) => set('token', e.target.value)}
            placeholder={savedHintToken(saved ?? null)}
            style={inputStyle}
          />
        </FormField>
      );
    case 'api_key_header':
      return (
        <>
          <FormField label="Header name" required>
            <input
              value={String(fields.header_name ?? '')}
              onChange={(e) => set('header_name', e.target.value)}
              placeholder="x-api-key"
              style={inputStyle}
            />
          </FormField>
          <FormField label="Header value" required>
            <input
              type="password"
              value={String(fields.value ?? '')}
              onChange={(e) => set('value', e.target.value)}
              placeholder={savedHintValue(saved ?? null)}
              style={inputStyle}
            />
          </FormField>
        </>
      );
    case 'api_key_query':
      return (
        <>
          <FormField label="Query param name" required>
            <input
              value={String(fields.param_name ?? '')}
              onChange={(e) => set('param_name', e.target.value)}
              placeholder="apiKey"
              style={inputStyle}
            />
          </FormField>
          <FormField label="Param value" required>
            <input
              type="password"
              value={String(fields.value ?? '')}
              onChange={(e) => set('value', e.target.value)}
              placeholder={savedHintValue(saved ?? null)}
              style={inputStyle}
            />
          </FormField>
        </>
      );
    case 'basic':
      return (
        <>
          <FormField label="Username" required>
            <input
              value={String(fields.username ?? '')}
              onChange={(e) => set('username', e.target.value)}
              style={inputStyle}
            />
          </FormField>
          <FormField label="Password" required>
            <input
              type="password"
              value={String(fields.password ?? '')}
              onChange={(e) => set('password', e.target.value)}
              placeholder={savedHintValue(saved ?? null)}
              style={inputStyle}
            />
          </FormField>
        </>
      );
    case 'oauth_client_credentials':
      return (
        <>
          <FormField label="Token URL" required>
            <input
              value={String(fields.token_url ?? '')}
              onChange={(e) => set('token_url', e.target.value)}
              placeholder="https://realm.example.com/oauth2/token"
              style={inputStyle}
            />
          </FormField>
          <FormField label="Client ID" required>
            <input
              value={String(fields.client_id ?? '')}
              onChange={(e) => set('client_id', e.target.value)}
              style={inputStyle}
            />
          </FormField>
          <FormField label="Client secret" required>
            <input
              type="password"
              value={String(fields.client_secret ?? '')}
              onChange={(e) => set('client_secret', e.target.value)}
              placeholder={savedHintValue(saved ?? null)}
              style={inputStyle}
            />
          </FormField>
          <FormField label="Scope (optional)">
            <input
              value={String(fields.scope ?? '')}
              onChange={(e) => set('scope', e.target.value)}
              style={inputStyle}
            />
          </FormField>
        </>
      );
    case 'custom_headers': {
      const hdrs = (fields.headers ?? {}) as Record<string, string>;
      const entries = Object.entries(hdrs);
      const updateAt = (idx: number, name: string, value: string): void => {
        const next: Record<string, string> = {};
        entries.forEach(([k, v], i) => {
          if (i === idx) next[name] = value;
          else next[k] = v;
        });
        set('headers', next);
      };
      return (
        <FormField label="Headers" required>
          <div style={{ display: 'grid', gap: 6 }}>
            {entries.map(([k, v], idx) => (
              <div key={idx} style={{ display: 'flex', gap: 6 }}>
                <input
                  value={k}
                  onChange={(e) => updateAt(idx, e.target.value, v)}
                  placeholder="header name"
                  style={{ ...inputStyle, flex: '1 1 160px' }}
                />
                <input
                  type="password"
                  value={v}
                  onChange={(e) => updateAt(idx, k, e.target.value)}
                  placeholder="value"
                  style={{ ...inputStyle, flex: '2 1 240px' }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const next = { ...hdrs };
                    delete next[k];
                    set('headers', next);
                  }}
                  style={btnSecondary}
                  title="Remove"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => set('headers', { ...hdrs, '': '' })}
              style={{ ...btnSecondary, alignSelf: 'flex-start' }}
            >
              <Plus size={13} style={{ marginRight: 6 }} /> Add header
            </button>
          </div>
        </FormField>
      );
    }
  }
}

export function initialCredFieldsFromSaved(saved: WrapperCredentialView | null): CredFields {
  if (!saved) return {};
  const f = saved.fields;
  const out: CredFields = {};
  for (const [k, v] of Object.entries(f)) {
    if (typeof v === 'string') out[k] = v;
    else if (Array.isArray(v) && v.every((x) => typeof x === 'string')) {
      out.headers = Object.fromEntries((v as string[]).map((n) => [n, '']));
    } else if (v && typeof v === 'object') {
      out[k] = v as Record<string, string>;
    }
  }
  return out;
}

export function cleanCredFields(
  mode: WrapperCredentialMode,
  fields: CredFields,
): Record<string, unknown> {
  const f: Record<string, unknown> = { ...fields };
  if (mode === 'custom_headers') {
    const h = (f.headers ?? {}) as Record<string, string>;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(h)) {
      if (k.trim() && v) out[k.trim()] = v;
    }
    f.headers = out;
  }
  return f;
}

/**
 * Returns true when the given (mode, fields) pair has a fillable secret
 * we can ship to the API. False if the user only declared a mode but
 * left required secrets blank — caller can use this to decide whether
 * to upsert or skip.
 */
export function credIsFillable(mode: WrapperCredentialMode, fields: CredFields): boolean {
  switch (mode) {
    case 'none':
      return true;
    case 'bearer':
      return Boolean(fields.token);
    case 'api_key_header':
      return Boolean(fields.header_name && fields.value);
    case 'api_key_query':
      return Boolean(fields.param_name && fields.value);
    case 'basic':
      return Boolean(fields.username && fields.password);
    case 'oauth_client_credentials':
      return Boolean(fields.token_url && fields.client_id && fields.client_secret);
    case 'custom_headers': {
      const h = (fields.headers ?? {}) as Record<string, string>;
      return Object.entries(h).some(([k, v]) => k.trim() && v);
    }
  }
}

function savedHintToken(saved: WrapperCredentialView | null): string {
  return saved?.fields.has_token ? '••••••• (already saved — type to replace)' : '';
}
function savedHintValue(saved: WrapperCredentialView | null): string {
  const has =
    Boolean(saved?.fields.has_value) ||
    Boolean(saved?.fields.has_password) ||
    Boolean(saved?.fields.has_client_secret);
  return has ? '••••••• (already saved — type to replace)' : '';
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: JSX.Element;
}): JSX.Element {
  return (
    <label style={{ display: 'block', marginBottom: 8 }}>
      <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
        {label}
        {required && <Required />}
      </span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--line)',
  borderRadius: 6,
  fontSize: 13,
  background: 'transparent',
  color: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
};
const btnSecondary: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid var(--line-strong)',
  background: 'rgba(255,255,255,0.85)',
  fontSize: 13,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
};
